import React, { useMemo, useState, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  FileText, Download, Filter, Building2, MapPin, Calendar,
  TrendingUp, AlertCircle, PawPrint, Dog, MessageSquareWarning,
  Siren, CheckCircle, RotateCcw, Loader2, ChevronDown, ArrowUpDown
} from 'lucide-react';
import useFirebaseData from '../hooks/useFirebaseData';
import usePetRegistrations from '../hooks/usePetRegistrations';
import useComplaints from '../hooks/useComplaints';
import useDogBiteReports from '../hooks/useDogBiteReports';
import useUnionCouncils from '../hooks/useUnionCouncils';
import { useFilters } from '../context/FilterContext';
import { DOG_TYPES } from '../constants';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const ReportTable = () => {
  const { darkMode } = useOutletContext();
  const [groupBy, setGroupBy] = useState('tehsil'); // 'tehsil', 'uc', 'all'
  const [selectedCategories, setSelectedCategories] = useState(['all']);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ step: 0, message: '' });
  const [resultLimit, setResultLimit] = useState('all'); // 'all', '10', '20', '50'
  const [sortBy, setSortBy] = useState('operations_desc'); // sorting state
  const tableRef = useRef(null);

  const exportSteps = [
    'Preparing data...',
    'Creating PDF document...',
    'Adding header and summary...',
    'Generating table...',
    'Finalizing document...',
    'Downloading PDF...'
  ];

  const {
    selectedDistrict,
    selectedTahsil,
    selectedUC,
    timeRange,
    startDate,
    endDate,
    availableDistricts,
    availableTahsils,
    setSelectedTahsil,
    setSelectedUC,
    setTimeRange,
    setStartDate,
    setEndDate,
    resetFilters,
    ucLookup,
    hasActiveFilters,
    filters
  } = useFilters();

  // Fetch all data
  const { data: firebaseData, loading: opsLoading } = useFirebaseData(filters);
  const { data: petRegistrations, loading: petLoading } = usePetRegistrations(filters);
  const { data: complaints, loading: complaintsLoading } = useComplaints(filters);
  const { data: dogBiteReports, loading: biteLoading } = useDogBiteReports(filters);
  useUnionCouncils(filters); // For UC lookup data

  const loading = opsLoading || petLoading || complaintsLoading || biteLoading;

  // Category definitions
  const categories = [
    { id: 'all', label: 'All Categories', icon: TrendingUp },
    { id: 'operations', label: 'Dog Operations', icon: TrendingUp },
    { id: 'strayDogs', label: 'Stray Dogs', icon: AlertCircle },
    { id: 'petDogs', label: 'Pet Dogs', icon: PawPrint },
    { id: 'petRegistered', label: 'Pet Registrations', icon: Dog },
    { id: 'complaints', label: 'Public Complaints', icon: MessageSquareWarning },
    { id: 'dogBiteIncidents', label: 'Dog Bite Incidents', icon: Siren },
    { id: 'actionTaken', label: 'Action Taken', icon: CheckCircle },
  ];

  // Get available UCs filtered by scope, selected District and Tahsil
  const availableUCs = useMemo(() => {
    const ucs = [];
    Object.entries(ucLookup).forEach(([ucId, ucData]) => {
      // Scope enforcement: only show UCs within available districts
      if (ucData.district && !availableDistricts.includes(ucData.district)) return;
      // Filter by selected district
      if (selectedDistrict && ucData.district && ucData.district !== selectedDistrict) return;
      if (selectedTahsil) {
        if (ucData.tahsil === selectedTahsil) {
          ucs.push({ value: ucId, label: ucData.uc, tahsil: ucData.tahsil });
        }
      } else {
        ucs.push({ value: ucId, label: ucData.uc, tahsil: ucData.tahsil });
      }
    });
    return ucs.sort((a, b) => {
      const numA = parseInt(a.label?.replace(/\D/g, '')) || 0;
      const numB = parseInt(b.label?.replace(/\D/g, '')) || 0;
      return numA - numB;
    });
  }, [selectedTahsil, selectedDistrict, ucLookup, availableDistricts]);

  // Calculate report data grouped by tehsil or UC
  const reportData = useMemo(() => {
    const results = [];

    if (groupBy === 'tehsil') {
      // Group by Tehsil
      const tehsilsToShow = selectedTahsil ? [selectedTahsil] : availableTahsils;

      tehsilsToShow.forEach(tehsil => {
        const tehsilOps = firebaseData.filter(d => d.tahsil === tehsil);
        const tehsilPets = petRegistrations.filter(d => d.tahsil === tehsil);
        const tehsilComplaints = complaints.filter(d => d.tahsil === tehsil);
        const tehsilBites = dogBiteReports.filter(d => d.tahsil === tehsil);

        results.push({
          name: tehsil,
          type: 'tehsil',
          operations: tehsilOps.length,
          strayDogs: tehsilOps.filter(d => d.dogType === DOG_TYPES.STRAY).length,
          petDogs: tehsilOps.filter(d => d.dogType === DOG_TYPES.PET).length,
          petRegistered: tehsilPets.length,
          complaints: tehsilComplaints.length,
          dogBiteIncidents: tehsilBites.length,
          actionTaken: tehsilBites.filter(d => d.actionTaken === true).length,
        });
      });
    } else if (groupBy === 'uc') {
      // Group by UC
      const ucsToProcess = selectedUC
        ? [{ value: selectedUC, label: ucLookup[selectedUC]?.uc || selectedUC, tahsil: ucLookup[selectedUC]?.tahsil }]
        : availableUCs;

      ucsToProcess.forEach(uc => {
        const ucOps = firebaseData.filter(d => d.ucId === uc.value || d.uc === uc.label);
        const ucPets = petRegistrations.filter(d => d.ucId === uc.value || d.uc === uc.label);
        const ucComplaints = complaints.filter(d => d.ucId === uc.value);
        const ucBites = dogBiteReports.filter(d => d.ucId === uc.value);

        // Only show UCs that have at least some data
        const hasData = ucOps.length > 0 || ucPets.length > 0 || ucComplaints.length > 0 || ucBites.length > 0;

        if (hasData || selectedUC) {
          results.push({
            name: uc.label,
            tehsil: uc.tahsil,
            type: 'uc',
            operations: ucOps.length,
            strayDogs: ucOps.filter(d => d.dogType === DOG_TYPES.STRAY).length,
            petDogs: ucOps.filter(d => d.dogType === DOG_TYPES.PET).length,
            petRegistered: ucPets.length,
            complaints: ucComplaints.length,
            dogBiteIncidents: ucBites.length,
            actionTaken: ucBites.filter(d => d.actionTaken === true).length,
          });
        }
      });
    } else {
      // Show all (summary)
      results.push({
        name: 'All Areas',
        type: 'all',
        operations: firebaseData.length,
        strayDogs: firebaseData.filter(d => d.dogType === DOG_TYPES.STRAY).length,
        petDogs: firebaseData.filter(d => d.dogType === DOG_TYPES.PET).length,
        petRegistered: petRegistrations.length,
        complaints: complaints.length,
        dogBiteIncidents: dogBiteReports.length,
        actionTaken: dogBiteReports.filter(d => d.actionTaken === true).length,
      });
    }

    return results;
  }, [groupBy, selectedTahsil, selectedUC, firebaseData, petRegistrations, complaints, dogBiteReports, availableUCs, ucLookup, availableTahsils]);

  // Apply sorting and result limit
  const limitedReportData = useMemo(() => {
    const sorted = [...reportData];

    // Apply sorting based on sortBy
    const [field, direction] = sortBy.split('_');
    sorted.sort((a, b) => {
      let aVal, bVal;

      if (field === 'name') {
        aVal = a.name || '';
        bVal = b.name || '';
        return direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      } else if (field === 'tehsil') {
        aVal = a.tehsil || '';
        bVal = b.tehsil || '';
        return direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      } else {
        // Numeric fields
        aVal = a[field] || 0;
        bVal = b[field] || 0;
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
    });

    if (resultLimit === 'all') return sorted;
    const limit = parseInt(resultLimit, 10);
    return sorted.slice(0, limit);
  }, [reportData, resultLimit, sortBy]);

  // Calculate totals (all data)
  const totals = useMemo(() => {
    return reportData.reduce((acc, row) => ({
      operations: acc.operations + row.operations,
      strayDogs: acc.strayDogs + row.strayDogs,
      petDogs: acc.petDogs + row.petDogs,
      petRegistered: acc.petRegistered + row.petRegistered,
      complaints: acc.complaints + row.complaints,
      dogBiteIncidents: acc.dogBiteIncidents + row.dogBiteIncidents,
      actionTaken: acc.actionTaken + row.actionTaken,
    }), {
      operations: 0,
      strayDogs: 0,
      petDogs: 0,
      petRegistered: 0,
      complaints: 0,
      dogBiteIncidents: 0,
      actionTaken: 0,
    });
  }, [reportData]);

  // Calculate totals for limited data (for table footer)
  const limitedTotals = useMemo(() => {
    return limitedReportData.reduce((acc, row) => ({
      operations: acc.operations + row.operations,
      strayDogs: acc.strayDogs + row.strayDogs,
      petDogs: acc.petDogs + row.petDogs,
      petRegistered: acc.petRegistered + row.petRegistered,
      complaints: acc.complaints + row.complaints,
      dogBiteIncidents: acc.dogBiteIncidents + row.dogBiteIncidents,
      actionTaken: acc.actionTaken + row.actionTaken,
    }), {
      operations: 0,
      strayDogs: 0,
      petDogs: 0,
      petRegistered: 0,
      complaints: 0,
      dogBiteIncidents: 0,
      actionTaken: 0,
    });
  }, [limitedReportData]);

  // Get visible columns based on selected categories
  const visibleColumns = useMemo(() => {
    if (selectedCategories.includes('all')) {
      return ['operations', 'strayDogs', 'petDogs', 'petRegistered', 'complaints', 'dogBiteIncidents', 'actionTaken'];
    }
    return selectedCategories;
  }, [selectedCategories]);

  // Column definitions
  const columnDefs = {
    operations: { label: 'Total Operations', color: 'blue' },
    strayDogs: { label: 'Stray Dogs', color: 'red' },
    petDogs: { label: 'Pet Dogs', color: 'violet' },
    petRegistered: { label: 'Pet Registered', color: 'cyan' },
    complaints: { label: 'Complaints', color: 'orange' },
    dogBiteIncidents: { label: 'Dog Bite Incidents', color: 'rose' },
    actionTaken: { label: 'Action Taken', color: 'teal' },
  };

  // Handle category toggle
  const handleCategoryToggle = (categoryId) => {
    if (categoryId === 'all') {
      setSelectedCategories(['all']);
    } else {
      setSelectedCategories(prev => {
        const newCategories = prev.filter(c => c !== 'all');
        if (newCategories.includes(categoryId)) {
          const filtered = newCategories.filter(c => c !== categoryId);
          return filtered.length === 0 ? ['all'] : filtered;
        } else {
          return [...newCategories, categoryId];
        }
      });
    }
  };

  // Export to PDF
  const exportToPDF = async () => {
    setIsExporting(true);
    setExportProgress({ step: 0, message: exportSteps[0] });

    try {
      // Step 1: Preparing data
      await new Promise(resolve => setTimeout(resolve, 300));
      setExportProgress({ step: 1, message: exportSteps[1] });

      const pdf = new jsPDF('landscape', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();

      // Colors
      const primaryColor = [59, 130, 246]; // Blue
      const headerBg = [30, 41, 59]; // Dark slate
      const lightBg = [248, 250, 252]; // Light gray

      // Header Section
      pdf.setFillColor(...primaryColor);
      pdf.rect(0, 0, pageWidth, 35, 'F');

      // Title
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text('WeWatch Report', 14, 18);

      // Subtitle
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Monitoring System - Statistics Report', 14, 27);

      // Date on right
      pdf.setFontSize(10);
      const dateStr = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      pdf.text(`Generated: ${dateStr}`, pageWidth - 14, 18, { align: 'right' });

      // Filter info
      let filterText = `Grouped by: ${groupBy === 'tehsil' ? 'Tehsil' : groupBy === 'uc' ? 'Union Council' : 'All'}`;
      if (resultLimit !== 'all') filterText += ` | Top ${resultLimit}`;
      if (selectedTahsil) filterText += ` | Tehsil: ${selectedTahsil}`;
      if (selectedUC) filterText += ` | UC: ${ucLookup[selectedUC]?.uc || selectedUC}`;
      if (startDate || endDate) {
        filterText += ` | Date: ${startDate || 'Start'} to ${endDate || 'End'}`;
      }
      pdf.text(filterText, pageWidth - 14, 27, { align: 'right' });

      // Step 2: Adding header and summary
      await new Promise(resolve => setTimeout(resolve, 200));
      setExportProgress({ step: 2, message: exportSteps[2] });

      // Summary Cards Section
      let yPos = 45;
      pdf.setFillColor(...lightBg);
      pdf.roundedRect(10, yPos, pageWidth - 20, 25, 3, 3, 'F');

      pdf.setTextColor(100, 116, 139);
      pdf.setFontSize(9);
      pdf.text('SUMMARY TOTALS', 16, yPos + 8);

      // Summary values
      const summaryItems = [
        { label: 'Operations', value: totals.operations, color: [59, 130, 246] },
        { label: 'Stray Dogs', value: totals.strayDogs, color: [239, 68, 68] },
        { label: 'Pet Dogs', value: totals.petDogs, color: [139, 92, 246] },
        { label: 'Pet Registered', value: totals.petRegistered, color: [6, 182, 212] },
        { label: 'Complaints', value: totals.complaints, color: [249, 115, 22] },
        { label: 'Dog Bites', value: totals.dogBiteIncidents, color: [244, 63, 94] },
        { label: 'Actions', value: totals.actionTaken, color: [20, 184, 166] },
      ];

      const cardWidth = (pageWidth - 40) / summaryItems.length;
      summaryItems.forEach((item, index) => {
        const x = 16 + (index * cardWidth);
        pdf.setTextColor(...item.color);
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text(item.value.toString(), x, yPos + 18);
        pdf.setTextColor(100, 116, 139);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text(item.label, x, yPos + 23);
      });

      // Step 3: Generating table
      await new Promise(resolve => setTimeout(resolve, 200));
      setExportProgress({ step: 3, message: exportSteps[3] });

      // Table Section
      yPos = 80;

      // Prepare table data
      const tableColumns = [
        { header: groupBy === 'tehsil' ? 'Tehsil' : groupBy === 'uc' ? 'Union Council' : 'Area', dataKey: 'name' },
      ];

      if (groupBy === 'uc') {
        tableColumns.push({ header: 'Tehsil', dataKey: 'tehsil' });
      }

      visibleColumns.forEach(col => {
        tableColumns.push({ header: columnDefs[col].label, dataKey: col });
      });

      const tableData = limitedReportData.map(row => {
        const rowData = { name: row.name };
        if (groupBy === 'uc') {
          rowData.tehsil = row.tehsil || '-';
        }
        visibleColumns.forEach(col => {
          rowData[col] = row[col];
        });
        return rowData;
      });

      // Add totals row
      const totalsRow = { name: resultLimit !== 'all' ? `TOTAL (Top ${resultLimit})` : 'TOTAL' };
      if (groupBy === 'uc') {
        totalsRow.tehsil = '';
      }
      visibleColumns.forEach(col => {
        totalsRow[col] = limitedTotals[col];
      });
      tableData.push(totalsRow);

      // Draw table
      autoTable(pdf, {
        startY: yPos,
        head: [tableColumns.map(col => col.header)],
        body: tableData.map(row => tableColumns.map(col => row[col.dataKey])),
        theme: 'grid',
        headStyles: {
          fillColor: headerBg,
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 9,
          halign: 'center',
          cellPadding: 4,
        },
        bodyStyles: {
          fontSize: 9,
          cellPadding: 3,
          halign: 'center',
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252],
        },
        columnStyles: {
          0: { halign: 'left', fontStyle: 'bold' },
        },
        didParseCell: function (data) {
          // Style the totals row
          if (data.row.index === tableData.length - 1) {
            data.cell.styles.fillColor = [30, 41, 59];
            data.cell.styles.textColor = [255, 255, 255];
            data.cell.styles.fontStyle = 'bold';
          }
        },
        margin: { left: 10, right: 10 },
      });

      // Step 4: Finalizing document
      await new Promise(resolve => setTimeout(resolve, 200));
      setExportProgress({ step: 4, message: exportSteps[4] });

      // Footer
      const finalY = (pdf).lastAutoTable.finalY + 15;
      pdf.setDrawColor(200, 200, 200);
      pdf.line(10, finalY, pageWidth - 10, finalY);

      pdf.setTextColor(100, 116, 139);
      pdf.setFontSize(8);
      pdf.text('WeWatch - Monitoring System', 14, finalY + 8);
      pdf.text('Developed by Nexis Tech Solutions', pageWidth / 2, finalY + 8, {
        align: 'center',
        link: { url: 'https://wa.me/923007722777' }
      });
      pdf.text(`Page 1 of 1`, pageWidth - 14, finalY + 8, { align: 'right' });

      // Step 5: Downloading PDF
      await new Promise(resolve => setTimeout(resolve, 200));
      setExportProgress({ step: 5, message: exportSteps[5] });

      // Save PDF
      const fileName = `WeWatch_Report_${groupBy}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

      // Small delay to show completion
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsExporting(false);
      setExportProgress({ step: 0, message: '' });
    }
  };

  // Styles
  const selectClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-all focus:ring-2 focus:ring-blue-500/20 ${darkMode
    ? 'bg-gray-700/50 border-gray-600 text-white hover:border-gray-500'
    : 'bg-gray-50 border-gray-200 text-gray-900 hover:border-gray-300'
    }`;

  const labelClass = `flex items-center gap-1.5 text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`;

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-xl ${darkMode ? 'bg-blue-900/50' : 'bg-blue-50'}`}>
            <FileText size={24} className={darkMode ? 'text-blue-400' : 'text-blue-600'} />
          </div>
          <div>
            <h1 className={`text-xl sm:text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Report Table
            </h1>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              View and export statistics by Tehsil or Union Council
            </p>
          </div>
        </div>

        <button
          onClick={exportToPDF}
          disabled={isExporting || loading}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${isExporting || loading
            ? 'bg-gray-400 cursor-not-allowed text-white'
            : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/25'
            }`}
        >
          {isExporting ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Generating PDF...
            </>
          ) : (
            <>
              <Download size={18} />
              Export PDF
            </>
          )}
        </button>
      </div>

      {/* Filters Section */}
      <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} rounded-xl shadow-sm border p-4 sm:p-5 mb-6`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className={`p-2 rounded-lg ${darkMode ? 'bg-blue-900/50' : 'bg-blue-50'}`}>
              <Filter size={16} className={darkMode ? 'text-blue-400' : 'text-blue-600'} />
            </div>
            <span className={`font-medium text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Filters</span>
          </div>
          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
            >
              <RotateCcw size={12} />
              Clear All
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 sm:gap-4">
          {/* Group By */}
          <div className="space-y-1.5">
            <label className={labelClass}>
              <ChevronDown size={12} />
              Group By
            </label>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              className={selectClass}
            >
              <option value="tehsil">By Tehsil</option>
              <option value="uc">By Union Council</option>
              <option value="all">Summary Only</option>
            </select>
          </div>

          {/* Result Limit */}
          <div className="space-y-1.5">
            <label className={labelClass}>
              <Filter size={12} />
              Show Results
            </label>
            <select
              value={resultLimit}
              onChange={(e) => setResultLimit(e.target.value)}
              className={selectClass}
            >
              <option value="all">All Results</option>
              <option value="10">Top 10</option>
              <option value="20">Top 20</option>
              <option value="50">Top 50</option>
            </select>
          </div>

          {/* Sort By */}
          <div className="space-y-1.5">
            <label className={labelClass}>
              <ArrowUpDown size={12} />
              Sort By
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className={selectClass}
            >
              <option value="operations_desc">Operations (High to Low)</option>
              <option value="operations_asc">Operations (Low to High)</option>
              <option value="name_asc">Name (A-Z)</option>
              <option value="name_desc">Name (Z-A)</option>
              <option value="strayDogs_desc">Stray Dogs (High to Low)</option>
              <option value="petDogs_desc">Pet Dogs (High to Low)</option>
              <option value="petRegistered_desc">Pet Registered (High to Low)</option>
              <option value="complaints_desc">Complaints (High to Low)</option>
              <option value="dogBiteIncidents_desc">Dog Bites (High to Low)</option>
              <option value="actionTaken_desc">Actions (High to Low)</option>
            </select>
          </div>

          {/* Tehsil */}
          <div className="space-y-1.5">
            <label className={labelClass}>
              <Building2 size={12} />
              Tehsil
            </label>
            <select
              value={selectedTahsil}
              onChange={(e) => setSelectedTahsil(e.target.value)}
              className={selectClass}
            >
              <option value="">All Tehsils</option>
              {availableTahsils.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {/* Union Council */}
          <div className="space-y-1.5">
            <label className={labelClass}>
              <MapPin size={12} />
              Union Council
            </label>
            <select
              value={selectedUC}
              onChange={(e) => setSelectedUC(e.target.value)}
              className={selectClass}
              disabled={groupBy === 'tehsil'}
            >
              <option value="">All UCs</option>
              {availableUCs.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
            </select>
          </div>

          {/* Time Range */}
          <div className="space-y-1.5">
            <label className={labelClass}>
              <Calendar size={12} />
              Time Range
            </label>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className={selectClass}
            >
              <option value="All Time">All Time</option>
              <option value="This Week">This Week</option>
              <option value="This Month">This Month</option>
              <option value="Last Month">Last Month</option>
              <option value="Custom">Custom</option>
            </select>
          </div>

          {/* Start Date */}
          <div className="space-y-1.5">
            <label className={labelClass}>
              <Calendar size={12} />
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className={selectClass}
            />
          </div>

          {/* End Date */}
          <div className="space-y-1.5">
            <label className={labelClass}>
              <Calendar size={12} />
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className={selectClass}
            />
          </div>
        </div>

        {/* Category Filters */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <label className={`${labelClass} mb-2`}>
            <Filter size={12} />
            Data Categories
          </label>
          <div className="flex flex-wrap gap-2">
            {categories.map(cat => {
              const isSelected = selectedCategories.includes(cat.id);
              const Icon = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryToggle(cat.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${isSelected
                    ? 'bg-blue-600 text-white'
                    : darkMode
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  <Icon size={14} />
                  {cat.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        {[
          { key: 'operations', label: 'Operations', value: totals.operations, color: 'blue', icon: TrendingUp },
          { key: 'strayDogs', label: 'Stray Dogs', value: totals.strayDogs, color: 'red', icon: AlertCircle },
          { key: 'petDogs', label: 'Pet Dogs', value: totals.petDogs, color: 'violet', icon: PawPrint },
          { key: 'petRegistered', label: 'Pet Registered', value: totals.petRegistered, color: 'cyan', icon: Dog },
          { key: 'complaints', label: 'Complaints', value: totals.complaints, color: 'orange', icon: MessageSquareWarning },
          { key: 'dogBiteIncidents', label: 'Dog Bites', value: totals.dogBiteIncidents, color: 'rose', icon: Siren },
          { key: 'actionTaken', label: 'Actions', value: totals.actionTaken, color: 'teal', icon: CheckCircle },
        ].map(item => {
          const Icon = item.icon;
          const colorClasses = {
            blue: darkMode ? 'bg-blue-900/30 text-blue-400 border-blue-800' : 'bg-blue-50 text-blue-600 border-blue-200',
            red: darkMode ? 'bg-red-900/30 text-red-400 border-red-800' : 'bg-red-50 text-red-600 border-red-200',
            violet: darkMode ? 'bg-violet-900/30 text-violet-400 border-violet-800' : 'bg-violet-50 text-violet-600 border-violet-200',
            cyan: darkMode ? 'bg-cyan-900/30 text-cyan-400 border-cyan-800' : 'bg-cyan-50 text-cyan-600 border-cyan-200',
            orange: darkMode ? 'bg-orange-900/30 text-orange-400 border-orange-800' : 'bg-orange-50 text-orange-600 border-orange-200',
            rose: darkMode ? 'bg-rose-900/30 text-rose-400 border-rose-800' : 'bg-rose-50 text-rose-600 border-rose-200',
            teal: darkMode ? 'bg-teal-900/30 text-teal-400 border-teal-800' : 'bg-teal-50 text-teal-600 border-teal-200',
          };
          return (
            <div
              key={item.key}
              className={`p-3 rounded-xl border ${colorClasses[item.color]}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon size={14} />
                <span className="text-xs font-medium opacity-80">{item.label}</span>
              </div>
              <p className="text-xl font-bold">{item.value}</p>
            </div>
          );
        })}
      </div>

      {/* Limited Results Info */}
      {resultLimit !== 'all' && limitedReportData.length > 0 && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-xl mb-4 ${darkMode ? 'bg-blue-900/30 border-blue-800 text-blue-300' : 'bg-blue-50 border-blue-200 text-blue-700'} border`}>
          <AlertCircle size={16} />
          <span className="text-sm">
            Showing top {resultLimit} results sorted by total operations. {reportData.length > parseInt(resultLimit) && `(${reportData.length - parseInt(resultLimit)} more hidden)`}
          </span>
        </div>
      )}

      {/* Data Table */}
      <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} rounded-xl shadow-sm border overflow-hidden`}>
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={32} className={`animate-spin ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
          </div>
        ) : (
          <div className="overflow-x-auto" ref={tableRef}>
            <table className="w-full">
              <thead>
                <tr className={darkMode ? 'bg-gray-900' : 'bg-gray-50'}>
                  <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {groupBy === 'tehsil' ? 'Tehsil' : groupBy === 'uc' ? 'Union Council' : 'Area'}
                  </th>
                  {groupBy === 'uc' && (
                    <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Tehsil
                    </th>
                  )}
                  {visibleColumns.map(col => (
                    <th
                      key={col}
                      className={`px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                    >
                      {columnDefs[col].label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {limitedReportData.map((row, index) => (
                  <tr
                    key={index}
                    className={`${darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'} transition-colors`}
                  >
                    <td className={`px-4 py-3 text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {row.name}
                    </td>
                    {groupBy === 'uc' && (
                      <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {row.tehsil || '-'}
                      </td>
                    )}
                    {visibleColumns.map(col => (
                      <td
                        key={col}
                        className={`px-4 py-3 text-sm text-center font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
                      >
                        {row[col]}
                      </td>
                    ))}
                  </tr>
                ))}
                {/* Totals Row */}
                <tr className={darkMode ? 'bg-gray-900' : 'bg-gray-100'}>
                  <td className={`px-4 py-3 text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {resultLimit !== 'all' ? `TOTAL (Top ${resultLimit})` : 'TOTAL'}
                  </td>
                  {groupBy === 'uc' && (
                    <td className="px-4 py-3 text-sm"></td>
                  )}
                  {visibleColumns.map(col => (
                    <td
                      key={col}
                      className={`px-4 py-3 text-sm text-center font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}
                    >
                      {limitedTotals[col]}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Empty State */}
        {!loading && limitedReportData.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20">
            <FileText size={48} className={`mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
            <p className={`text-lg font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No data available
            </p>
            <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Try adjusting your filters
            </p>
          </div>
        )}
      </div>

      {/* PDF Export Progress Modal */}
      {isExporting && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl shadow-2xl border p-8 max-w-md w-full mx-4`}>
            <div className="flex flex-col items-center">
              {/* Animated Icon */}
              <div className={`p-4 rounded-full mb-6 ${darkMode ? 'bg-blue-900/50' : 'bg-blue-50'}`}>
                <Download size={32} className={`${darkMode ? 'text-blue-400' : 'text-blue-600'} animate-bounce`} />
              </div>

              {/* Title */}
              <h3 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Generating PDF Report
              </h3>

              {/* Current Step */}
              <p className={`text-sm mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {exportProgress.message}
              </p>

              {/* Progress Bar */}
              <div className="w-full mb-4">
                <div className={`h-2 rounded-full overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <div
                    className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${((exportProgress.step + 1) / exportSteps.length) * 100}%` }}
                  />
                </div>
                <div className="flex justify-between mt-2">
                  <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    Step {exportProgress.step + 1} of {exportSteps.length}
                  </span>
                  <span className={`text-xs font-medium ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                    {Math.round(((exportProgress.step + 1) / exportSteps.length) * 100)}%
                  </span>
                </div>
              </div>

              {/* Steps List */}
              <div className="w-full space-y-2 mt-2">
                {exportSteps.map((step, index) => (
                  <div
                    key={index}
                    className={`flex items-center gap-3 text-sm ${index < exportProgress.step
                      ? darkMode ? 'text-green-400' : 'text-green-600'
                      : index === exportProgress.step
                        ? darkMode ? 'text-blue-400' : 'text-blue-600'
                        : darkMode ? 'text-gray-600' : 'text-gray-300'
                      }`}
                  >
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${index < exportProgress.step
                      ? 'bg-green-500 text-white'
                      : index === exportProgress.step
                        ? darkMode ? 'bg-blue-500' : 'bg-blue-600'
                        : darkMode ? 'bg-gray-700' : 'bg-gray-200'
                      }`}>
                      {index < exportProgress.step ? (
                        <CheckCircle size={12} />
                      ) : index === exportProgress.step ? (
                        <Loader2 size={12} className="animate-spin text-white" />
                      ) : (
                        <span className="text-xs">{index + 1}</span>
                      )}
                    </div>
                    <span className={index <= exportProgress.step ? 'font-medium' : ''}>
                      {step}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportTable;
